import React, { useState, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';
import {
    Box, Typography, Button, Avatar, Chip, Stack, InputAdornment, TextField,
    Snackbar, Alert, Tooltip, Skeleton, IconButton, Tabs, Tab,
} from '@mui/material';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import PersonAddAlt1RoundedIcon from '@mui/icons-material/PersonAddAlt1Rounded';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CircularProgress from '@mui/material/CircularProgress';
import { useNavigate, useLocation, useOutletContext, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectUserTypeId, USER_TYPE } from '../redux/slices/authSlice';
import http, { apiErrorMessage } from '../Api/http';
import { GetEmployees } from '../Api/Api';
import { fmtApiDate } from '../utils/apiFields';
import { initialsFromName, cleanName, avatarTone, companyExperience } from '../utils/format';
import SearchSelect from '../components/SearchSelect';
import useOrgOptions from '../hooks/useOrgOptions';
import PartTimeTab from '../components/EmployeeComps/PartTimeTab';

const PRIMARY = '#7C5CFC';
const PRIMARY_LIGHT = '#F1EEFE';
const PRIMARY_BORDER = '#C9BEFB';
const PAGE_SIZE = 20;
const card = { bgcolor: '#fff', border: '1px solid #E6EAF1', borderRadius: '7px', boxShadow: '0 1px 3px rgba(16,24,40,0.06)' };
const tonalBtn = { bgcolor: PRIMARY_LIGHT, color: PRIMARY, border: `1px solid ${PRIMARY_BORDER}`, fontWeight: 700, borderRadius: '7px', boxShadow: 'none', textTransform: 'none', '&:hover': { bgcolor: '#E7DFFC' } };
const ghostBtn = { color: '#475569', bgcolor: '#fff', border: '1px solid #E5E7EB', fontWeight: 700, borderRadius: '7px', textTransform: 'none', '&:hover': { bgcolor: '#F8FAFC' } };

const initials = initialsFromName;

const fmtDate = (v) => fmtApiDate(v) || '—';

// The date an inactive employee left. The list API's field name for it isn't pinned
// down, so the likely ones are tried in turn.
const leftOn = (r) => r.relieveDate || r.lastWorkingDay || r.relievedOn || r.relievingDate || r.dateOfLeaving || r.inactiveOn || r.inactivatedOn || null;
// Added straight as a former employee (no onboarding, no login), as opposed to someone who was active and later moved to inactive.
const isDirectRelieved = (r) => r.onboardedAsRelieved === true;
const isActive = (r) => String(r.status || '').toLowerCase() === 'active';
const STATUS_TABS = [
    { key: 'all', label: 'All' },
    { key: 'active', label: 'Active' },
    { key: 'inactive', label: 'Inactive' },
];

function EmployeesList() {
    const navigate = useNavigate();
    const location = useLocation();
    const userTypeId = useSelector(selectUserTypeId);
    const canBulkImport = userTypeId === USER_TYPE.MASTER_ADMIN || userTypeId === USER_TYPE.ADMIN;

    // The page actions live at the right end of the breadcrumb row.
    const { setCrumbActions } = useOutletContext() || {};
    useEffect(() => {
        if (!setCrumbActions) return undefined;
        setCrumbActions(
            <Box sx={{ display: 'flex', gap: 1 }}>
                {canBulkImport && (
                    <Button
                        startIcon={<UploadFileRoundedIcon sx={{ color: PRIMARY }} />}
                        onClick={() => navigate('/dashboard/employees/bulk-import')}
                        sx={{ ...ghostBtn, height: 42, px: 2, color: '#334155', '&:hover': { bgcolor: PRIMARY_LIGHT, borderColor: PRIMARY_BORDER, color: PRIMARY } }}
                    >
                        Bulk Import
                    </Button>
                )}
                <Button startIcon={<PersonAddAlt1RoundedIcon />} onClick={() => navigate('/dashboard/employees/onboard')} sx={{ ...tonalBtn, height: 42, px: 2.2 }}>
                    Onboard Employee
                </Button>
            </Box>,
        );
        return () => setCrumbActions(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canBulkImport, setCrumbActions]);

    const [search, setSearch] = useState('');
    const [query, setQuery] = useState('');
    const [page, setPage] = useState(1);
    const [statusFilter, setStatusFilter] = useState('all');
    const [deptFilter, setDeptFilter] = useState('all');
    const { departmentNames } = useOrgOptions();
    const [exporting, setExporting] = useState(false);

    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [snack, setSnack] = useState({ msg: '', severity: 'success' });

    useEffect(() => {
        if (location.state?.toast || location.state?.statusFilter) {
            if (location.state.toast) setSnack({ msg: location.state.toast, severity: location.state.severity || 'success' });
            // Coming back from the inactive-employee form lands on the Inactive tab.
            if (location.state.statusFilter) { setStatusFilter(location.state.statusFilter); setPage(1); }
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location, navigate]);

    useEffect(() => {
        const t = setTimeout(() => { setQuery(search.trim()); setPage(1); }, 400);
        return () => clearTimeout(t);
    }, [search]);

    // Only the newest request may write the table: a slow earlier request (e.g. "All") must not overwrite
    // the result of a later one (e.g. "Inactive").
    const loadSeq = React.useRef(0);
    const load = useCallback(async () => {
        const seq = ++loadSeq.current;
        setLoading(true);
        setLoadError('');
        try {
            // The list API filters by status and pages on the server.
            const byStatus = statusFilter !== 'all';
            // The list API has no department filter, so with one chosen the whole list is fetched and paged here.
            const byDept = deptFilter !== 'all';
            const size = byDept ? 1000 : PAGE_SIZE;
            const pageNo = byDept ? 1 : page;
            const { data: body } = await http.get(GetEmployees, {
                params: byStatus ? { search: query, status: statusFilter, page: pageNo, pageSize: size } : { search: query, page: pageNo, pageSize: size },
            });
            const noRecords = body?.error && /not found|no employee|no record|no data/i.test(body.message || '');
            if (body?.error && !noRecords) throw new Error(body.message || 'Could not load employees.');
            if (seq !== loadSeq.current) return;
            const d = body?.data || {};
            // Safety net: never show an employee under the wrong status tab.
            const items = (d.items || []).filter((r) => statusFilter === 'all' || isActive(r) === (statusFilter === 'active'));
            if (byDept) {
                const inDept = items.filter((r) => String(r.department || '').trim().toLowerCase() === deptFilter.toLowerCase());
                setRows(inDept.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE));
                setTotal(inDept.length);
            } else {
                setRows(items);
                setTotal(statusFilter === 'all' || items.length === (d.items || []).length ? (d.total || 0) : items.length);
            }
        } catch (err) {
            if (seq !== loadSeq.current) return;
            // The API answers an empty list with HTTP 404 ("No employees found"): that is an empty
            // table, not a failure, so it falls through to the "no employees" message below.
            const empty = err?.response?.status === 404;
            setLoadError(empty ? '' : (err?.response || err?.request ? apiErrorMessage(err, 'Could not load employees.') : err.message));
            setRows([]);
            setTotal(0);
        } finally {
            if (seq === loadSeq.current) setLoading(false);
        }
    }, [query, page, statusFilter, deptFilter]);

    useEffect(() => { load(); }, [load]);

    const [org, setOrg] = useState({ total: null, active: null, departments: null });
    const loadOrgStats = useCallback(async () => {
        try {
            const { data: body } = await http.get(GetEmployees, {
                params: { search: '', page: 1, pageSize: 1000 },
            });
            const noRecords = body?.error && /not found|no employee|no record|no data/i.test(body.message || '');
            if (body?.error && !noRecords) { setOrg({ total: 0, active: 0, departments: 0 }); return; }
            const d = body?.data || {};
            const items = d.items || [];
            const n = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
            const total = n(d.total ?? items.length);
            const active = d.activeCount != null
                ? n(d.activeCount)
                : items.filter((r) => String(r.status || '').toLowerCase() === 'active').length;
            const departments = d.departmentCount != null
                ? n(d.departmentCount)
                : new Set(items.map((r) => r.department).filter(Boolean)).size;
            setOrg({ total, active, departments });
        } catch {
            setOrg({ total: 0, active: 0, departments: 0 });
        }
    }, []);
    useEffect(() => { loadOrgStats(); }, [loadOrgStats]);

    // Same approach as the Attendance report: build the sheet with xlsx and download it.
    // It exports every employee that matches the current search and Active / Inactive
    // tab (not just the visible page), so it fetches the full list first.
    const handleExport = async () => {
        setExporting(true);
        try {
            const { data: body } = await http.get(GetEmployees, { params: { search: query, page: 1, pageSize: 1000 } });
            const noRecords = body?.error && /not found|no employee|no record|no data/i.test(body.message || '');
            if (body?.error && !noRecords) throw new Error(body.message || 'Could not load employees.');
            let items = body?.data?.items || [];
            if (statusFilter !== 'all') items = items.filter((r) => isActive(r) === (statusFilter === 'active'));
            if (deptFilter !== 'all') items = items.filter((r) => String(r.department || '').trim().toLowerCase() === deptFilter.toLowerCase());
            if (!items.length) { setSnack({ msg: 'There are no employees to export.', severity: 'warning' }); return; }

            const headers = ['S.No', 'Employee Name', 'Employee Code', 'Email', 'Mobile', 'Department', 'Designation', 'Joined', 'Experience (this company)', 'Left On', 'Status'];
            const rows = items.map((r, idx) => [
                idx + 1, cleanName(r.fullName), r.employeeCode || '', r.personalEmail || '', r.personalMobile || '',
                r.department || '', r.designation || '', fmtDate(r.dateOfJoining), companyExperience(r.dateOfJoining, r.status, leftOn(r)),
                isActive(r) ? '' : fmtDate(leftOn(r)), isActive(r) ? 'Active' : (r.status || 'Inactive'),
            ]);
            const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
            ws['!cols'] = [{ wch: 6 }, { wch: 24 }, { wch: 14 }, { wch: 28 }, { wch: 14 }, { wch: 20 }, { wch: 22 }, { wch: 14 }, { wch: 22 }, { wch: 14 }, { wch: 10 }];
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Employees');
            const label = statusFilter === 'all' ? 'All' : statusFilter === 'active' ? 'Active' : 'Inactive';
            XLSX.writeFile(wb, `Employees_${label}_${new Date().toISOString().slice(0, 10)}.xlsx`);
        } catch (err) {
            setSnack({ msg: err?.response || err?.request ? apiErrorMessage(err, 'Could not export employees.') : (err.message || 'Could not export employees.'), severity: 'error' });
        } finally {
            setExporting(false);
        }
    };

    const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const from = total ? (page - 1) * PAGE_SIZE + 1 : 0;
    const to = Math.min(page * PAGE_SIZE, total);

    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ ...card, p: 0, overflow: 'hidden' }}>
                <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', p: 2.5, flexWrap: 'wrap', gap: 1.5, bgcolor: '#F7F6FD', borderBottom: '1px solid #EAE7F7' }}>
                    <Stack direction="row" spacing={1.2} sx={{ alignItems: 'center' }}>
                        <Box sx={{ width: 28, height: 28, borderRadius: '8px', bgcolor: '#fff', boxShadow: '0 1px 4px rgba(16,24,40,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <GroupsRoundedIcon sx={{ color: PRIMARY, fontSize: 18 }} />
                        </Box>
                        <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#0F172A' }}>All Employees</Typography>
                        <Chip label={loading ? 'Loading…' : `${total} record${total === 1 ? '' : 's'}`} size="small" sx={{ bgcolor: PRIMARY_LIGHT, color: PRIMARY, fontWeight: 700, fontSize: 11.5 }} />
                    </Stack>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                    <Stack direction="row" sx={{ bgcolor: '#fff', border: '1px solid #E6EAF1', borderRadius: '7px', p: '3px' }}>
                        {STATUS_TABS.map((t) => {
                            const count = t.key === 'all' ? org.total : t.key === 'active' ? org.active : (org.total != null && org.active != null ? org.total - org.active : null);
                            const on = statusFilter === t.key;
                            return (
                                <Box
                                    key={t.key} component="button" type="button" onClick={() => { setStatusFilter(t.key); setPage(1); }}
                                    sx={{
                                        border: 'none', cursor: 'pointer', px: 1.6, height: 30, borderRadius: '7px', fontSize: 12.5, fontWeight: 700,
                                        fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 0.7,
                                        bgcolor: on ? PRIMARY : 'transparent', color: on ? '#fff' : '#64748B',
                                        '&:hover': { bgcolor: on ? PRIMARY : PRIMARY_LIGHT },
                                    }}
                                >
                                    {t.label}
                                    {count != null && <Box component="span" sx={{ fontSize: 10.5, fontWeight: 800, px: 0.8, borderRadius: '10px', bgcolor: on ? 'rgba(255,255,255,0.25)' : '#F1F5F9', color: on ? '#fff' : '#64748B' }}>{count}</Box>}
                                </Box>
                            );
                        })}
                    </Stack>
                    <Box sx={{ width: { xs: '100%', sm: 200 } }}>
                        <SearchSelect
                            value={deptFilter} onChange={(e) => { setDeptFilter(e.target.value); setPage(1); }} searchPlaceholder="Search departments…"
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '7px', bgcolor: '#fff', fontSize: 13, height: 40 } }}
                            options={[{ value: 'all', label: 'All departments' }, ...departmentNames.map((n) => ({ value: n, label: n }))]}
                        />
                    </Box>
                    <TextField
                        placeholder="Search employees…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        size="small"
                        sx={{ width: { xs: '100%', sm: 260 }, '& .MuiOutlinedInput-root': { borderRadius: '7px', bgcolor: '#fff', fontSize: 13, height: 40 } }}
                        InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon sx={{ fontSize: 18, color: '#9CA3AF' }} /></InputAdornment> }}
                    />
                    {statusFilter === 'inactive' && (
                        <Button
                            startIcon={<PersonAddAlt1RoundedIcon sx={{ fontSize: 17 }} />}
                            onClick={() => navigate('/dashboard/employees/onboard', { state: { inactiveRecord: true } })}
                            sx={{ ...tonalBtn, height: 38, px: 1.8, fontSize: '12.5px' }}
                        >
                            Add Inactive Employee
                        </Button>
                    )}
                    <Button
                        size="small"
                        variant="contained"
                        startIcon={exporting ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <FileDownloadIcon sx={{ fontSize: 16 }} />}
                        onClick={handleExport}
                        disabled={exporting || loading || total === 0}
                        sx={{
                            textTransform: 'none', fontSize: '12.5px', fontWeight: 700,
                            bgcolor: PRIMARY, color: '#fff',
                            borderRadius: '7px', px: 1.8, height: 38,
                            boxShadow: `0 2px 6px ${PRIMARY}33`,
                            '&:hover': { bgcolor: '#6246E0', boxShadow: `0 4px 12px ${PRIMARY}55` },
                            '&.Mui-disabled': { bgcolor: '#E5E7EB', color: '#9CA3AF', boxShadow: 'none' },
                        }}
                    >
                        Export Excel
                    </Button>
                    </Stack>
                </Stack>
                <Box sx={{ overflowX: 'auto' }}>
                    <Box component="table" sx={{ width: '100%', minWidth: 820, borderCollapse: 'collapse' }}>
                        <Box component="thead" sx={{ bgcolor: '#F4F3FB' }}>
                            <Box component="tr">
                                {['EMPLOYEE', 'EMP ID', 'DEPARTMENT', 'JOINED', 'EXPERIENCE', 'STATUS', 'ACTIONS'].map((h) => (
                                    <Box component="th" key={h} sx={{ textAlign: h === 'ACTIONS' ? 'right' : 'left', ...(h === 'EMPLOYEE' && { width: 300 }), px: 2, py: 1.15, fontSize: 10.5, fontWeight: 700, color: '#6E6B99', letterSpacing: 0.6, borderBottom: '1px solid #E8E6F3', whiteSpace: 'nowrap' }}>{h}</Box>
                                ))}
                            </Box>
                        </Box>
                        <Box component="tbody">
                            {loading && Array.from({ length: 5 }).map((_, i) => (
                                <Box component="tr" key={`sk-${i}`}>
                                    {Array.from({ length: 7 }).map((__, j) => (
                                        <Box component="td" key={j} sx={{ px: 2.5, py: 1.7, borderBottom: '1px solid #EEF0F6' }}>
                                            <Skeleton variant="text" width={j === 0 ? 180 : 90} height={20} />
                                        </Box>
                                    ))}
                                </Box>
                            ))}

                            {!loading && rows.map((r, idx) => {
                                const active = isActive(r);
                                return (
                                    <Box component="tr" key={r.id} onClick={() => navigate(`/dashboard/employees/${r.id}`)} sx={{ cursor: 'pointer', bgcolor: isDirectRelieved(r) ? '#FFFBEB' : (idx % 2 ? '#FBFAFE' : '#fff'), opacity: active ? 1 : 0.8, '&:hover': { bgcolor: '#F5F4FC' }, transition: 'background-color .15s' }}>
                                        <Box component="td" sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid #EEF0F6' }}>
                                            <Stack direction="row" spacing={1.4} sx={{ alignItems: 'center' }}>
                                                <Avatar src={r.profilePhotoUrl || undefined} sx={{ width: 36, height: 36, ...avatarTone(r.fullName), fontSize: 13, fontWeight: 700, filter: active ? 'none' : 'grayscale(0.4)' }}>{initials(r.fullName)}</Avatar>
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#0F172A' }}>{cleanName(r.fullName)}</Typography>
                                                    <Typography sx={{ fontSize: 11.5, color: '#98A0AE' }}>{r.personalEmail || r.personalMobile || '—'}</Typography>
                                                </Box>
                                            </Stack>
                                        </Box>
                                        <Box component="td" sx={{ px: 2.5, py: 1.5, fontSize: 13, color: '#475569', fontWeight: 600, borderBottom: '1px solid #EEF0F6' }}>{isDirectRelieved(r) ? '—' : (r.employeeCode || '—')}</Box>
                                        <Box component="td" sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid #EEF0F6' }}><Chip label={r.department || '—'} size="small" sx={{ bgcolor: '#F1F5F9', fontSize: 11.5, fontWeight: 600 }} /></Box>
                                        <Box component="td" sx={{ px: 2.5, py: 1.5, fontSize: 13, color: '#475569', borderBottom: '1px solid #EEF0F6', whiteSpace: 'nowrap' }}>{fmtDate(r.dateOfJoining)}</Box>
                                        <Box component="td" sx={{ px: 2.5, py: 1.5, fontSize: 13, color: '#475569', fontWeight: 600, borderBottom: '1px solid #EEF0F6', whiteSpace: 'nowrap' }}>{companyExperience(r.dateOfJoining, r.status, leftOn(r)) || '—'}</Box>
                                        <Box component="td" sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid #EEF0F6' }}>
                                            <Chip label={active ? 'Active' : (isDirectRelieved(r) ? 'Relieved record' : (r.status || 'Inactive'))} size="small" sx={{ height: 22, fontSize: 11, fontWeight: 700, textTransform: 'capitalize', bgcolor: active ? '#DCFCE7' : (isDirectRelieved(r) ? '#FEF3C7' : '#FEE2E2'), color: active ? '#16A34A' : (isDirectRelieved(r) ? '#B45309' : '#DC2626') }} />
                                        </Box>
                                        <Box component="td" sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid #EEF0F6', textAlign: 'right' }}>
                                            <Tooltip arrow title="View details">
                                                <Button
                                                    onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/employees/${r.id}`); }}
                                                    size="small"
                                                    startIcon={<VisibilityRoundedIcon sx={{ fontSize: 16 }} />}
                                                    endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: 14 }} />}
                                                    sx={{ ...tonalBtn, height: 32, px: 1.4, fontSize: 12 }}
                                                >
                                                    View
                                                </Button>
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>

                    {!loading && loadError && (
                        <Stack direction="row" spacing={1.3} sx={{ alignItems: 'center', p: 2.5, bgcolor: '#FEF2F2', borderTop: '1px solid #FECACA' }}>
                            <ErrorOutlineRoundedIcon sx={{ fontSize: 22, color: '#B91C1C' }} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#991B1B' }}>Couldn't load employees</Typography>
                                <Typography sx={{ fontSize: 12, color: '#B91C1C' }}>{loadError}</Typography>
                            </Box>
                            <Button onClick={load} startIcon={<RefreshRoundedIcon sx={{ fontSize: 17 }} />} sx={{ textTransform: 'none', fontWeight: 700, fontSize: 12.5, color: '#B91C1C', bgcolor: '#fff', border: '1px solid #FECACA', borderRadius: '7px', height: 38, px: 2, '&:hover': { bgcolor: '#FEE2E2' } }}>Retry</Button>
                        </Stack>
                    )}

                    {!loading && !loadError && rows.length === 0 && (
                        <Box sx={{ textAlign: 'center', py: 6 }}>
                            <GroupsRoundedIcon sx={{ fontSize: 34, color: '#CBD2DD' }} />
                            <Typography sx={{ fontSize: 14, color: '#98A0AE', mt: 1 }}>
                                {query ? 'No employees match your search' : statusFilter !== 'all' ? `No ${statusFilter} employees found` : 'No employees found'}
                            </Typography>
                        </Box>
                    )}
                </Box>

                {!loadError && total > 0 && (
                    <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.15, borderTop: '1px solid #EEF0F6', bgcolor: '#FBFAFE', flexWrap: 'wrap', gap: 1 }}>
                        <Typography sx={{ fontSize: 12.5, color: '#64748B' }}>
                            Showing <strong>{from}–{to}</strong> of <strong>{total}</strong>
                        </Typography>
                        <Stack direction="row" spacing={0.8} sx={{ alignItems: 'center' }}>
                            <IconButton size="small" disabled={loading || page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
                                sx={{ border: '1px solid #E6EAF1', borderRadius: '7px', bgcolor: '#fff', width: 32, height: 32 }}>
                                <ChevronLeftRoundedIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#475569', px: 0.8 }}>Page {page} of {pageCount}</Typography>
                            <IconButton size="small" disabled={loading || page >= pageCount} onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                                sx={{ border: '1px solid #E6EAF1', borderRadius: '7px', bgcolor: '#fff', width: 32, height: 32 }}>
                                <ChevronRightRoundedIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Stack>
                    </Stack>
                )}
            </Box>

            <Snackbar open={Boolean(snack.msg)} autoHideDuration={snack.severity === 'warning' ? 8000 : 3000} onClose={() => setSnack((s) => ({ ...s, msg: '' }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                <Alert onClose={() => setSnack((s) => ({ ...s, msg: '' }))} severity={snack.severity} variant="filled" sx={{ borderRadius: '7px' }}>{snack.msg}</Alert>
            </Snackbar>
        </Box>
    );
}

const TABS = [
    { key: 'employees', label: 'Employees', icon: GroupsRoundedIcon },
    { key: 'part-time', label: 'Part Time', icon: ScheduleRoundedIcon },
];

export default function EmployeesPage() {
    const [params, setParams] = useSearchParams();
    const tabKey = TABS.some((t) => t.key === params.get('tab')) ? params.get('tab') : 'employees';
    return (
        <Box>
            <Box sx={{ p: 2, pb: 0 }}>
                <Box sx={{ ...card, px: 1 }}>
                    <Tabs
                        value={TABS.findIndex((t) => t.key === tabKey)}
                        onChange={(_, i) => setParams(TABS[i].key === 'employees' ? {} : { tab: TABS[i].key }, { replace: true })}
                        sx={{
                            minHeight: 52,
                            '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0', bgcolor: PRIMARY },
                            '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, fontSize: 13.5, color: '#64748B', minHeight: 52, px: 2 },
                            '& .Mui-selected': { color: `${PRIMARY} !important` },
                        }}
                    >
                        {TABS.map((t) => <Tab key={t.key} iconPosition="start" label={t.label} icon={<t.icon sx={{ fontSize: 18 }} />} />)}
                    </Tabs>
                </Box>
            </Box>
            {tabKey === 'part-time' ? <PartTimeTab /> : <EmployeesList />}
        </Box>
    );
}
